import { Injectable } from '@angular/core';
import {
  SessionService,
  User,
  Org,
  UsersService,
  PostSessionRequest,
  ValidateUserEmailRequest,
  PostSessionGoogleRequest,
  SignupWithGoogleRequest,
  OrganizationsService,
  SignupWithMicrosoftRequest,
} from 'ldt-identity-service-api';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { Log } from 'src/app/logger.service';
import { StatsigService } from '@statsig/angular-bindings';
import { environment } from 'src/environments/environment';

export interface UIOrg extends Org {
  role: string;
  contactCount?: number;
  roleSettings: {
    [key: string]: {
      [key: string]: boolean;
    };
  };
  settings: {
    people: {
      subType: string;
      capability: boolean;
      recordLimit: number | null;
      // Usage-credit balance pushed by billing; the enforced quota is
      // recordLimit (or the free-tier base) plus this.
      recordLimitCredit?: number;
      // 'monthly' opts into the legacy implicit free-tier allowance for an
      // unset limit; 'credit' (or unset) means an unset limit really is 0.
      freeTier?: 'credit' | 'monthly';
      features: any;
      subscriptionAnchorDate: string;
    };
    ledger: any;
    moneyball: any;
    clean: any;
    chat?: {
      subType?: string;
      capability?: boolean;
      costLimit?: number | null;
      snapshotQueryLimit?: number | 'unlimited' | null;
      deepResearchQueryLimit?: number | 'unlimited' | null;
      // Usage-credit balances pushed by billing, stacking on the base limits.
      costLimitCredit?: number;
      snapshotQueryLimitCredit?: number;
      deepResearchQueryLimitCredit?: number;
      // Same semantics as people.freeTier, for the chat limits.
      freeTier?: 'credit' | 'monthly';
      subscriptionAnchorDate?: string;
      features?: any;
    };
    // Identity-level org settings. The member cap lives here now; -1 (or no
    // value) means unlimited.
    identity?: {
      maxUsers?: number | 'unlimited' | null;
    };
  };
  peopleRecordCount: number | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  public redirectUrl = '/main';

  private loggedIn = new BehaviorSubject<boolean>(false);
  get $isLoggedIn(): Observable<boolean> {
    this.checkLogin();
    return this.loggedIn.asObservable();
  }
  public get isLoggedInValue(): boolean {
    return this.loggedIn.value;
  }
  private checkLogin(): boolean {
    // If we have the required items in localstorage, the user is logged in.
    // Their session _may_ be expired, but session handling will try to renew or
    //  invalidate their session. For purposes of this function, we don't deal
    //  with valid session, only that they have the required session properties in storage

    if (
      localStorage.getItem('accessToken') === null ||
      localStorage.getItem('user') === null ||
      localStorage.getItem('refreshToken') === null
    ) {
      this.loggedIn.next(false);
      return false;
    }

    this.setPalMode(false);
    this.loggedIn.next(true);
    return true;
  }

  private user = new BehaviorSubject<User>({
    id: 'none',
    email: 'none',
    createdAt: new Date().toISOString(),
    type: 'none',
    status: 'inactive',
    isValidated: false,
  });
  get $getUser(): Observable<User> {
    return this.user.asObservable();
  }
  public get getUserValue(): User {
    return this.user.value;
  }

  private selectedOrgId = new BehaviorSubject<string>('');
  get $getSelectedOrgId(): Observable<string> {
    return this.selectedOrgId.asObservable();
  }
  public get getSelectedOrgIdValue(): string {
    return this.selectedOrgId.value;
  }
  public async setSelectedOrgId(orgId: string) {
    if (this.getSelectedOrgIdValue === orgId) {
      return;
    }

    // This should never happen, but it's a good check
    if (!this.getOrgsValue.some((o) => o.id === orgId)) {
      Log.e('AuthService - setSelectedOrgId - orgId not found', orgId);
      return;
    }

    localStorage.setItem('selectedOrgId', orgId);
    this.selectedOrgId.next(orgId);

    try {
      await this.statsigService.updateUserAsync({
        userID: this.getUserValue.id,
        customIDs: {
          orgId: orgId,
        },
      });
    } catch (e) {
      Log.s('AuthService - error updating statsig user orgId', 'error');
    }
  }

  private selectedOrg = new BehaviorSubject<UIOrg | undefined>(undefined);
  get $getSelectedOrg(): Observable<UIOrg | undefined> {
    return this.selectedOrg.asObservable();
  }
  public get getSelectedOrgValue(): UIOrg | undefined {
    return this.selectedOrg.value;
  }
  private updateSelectedOrg(orgId: string) {
    let org = this.getOrgsValue.find((o) => {
      return o.id === orgId;
    });
    this.selectedOrg.next(org);
  }

  // Recompute all org-derived state for the currently selected org id.
  private refreshSelectedOrgState() {
    const orgId = this.getSelectedOrgIdValue;
    if (!orgId) {
      return;
    }

    // getSelectedOrg() self-heals a stale selection (e.g. an observed org
    // that's gone after a reload) by re-selecting the first available org.
    // That re-emits $getSelectedOrgId re-entrantly, which re-runs this
    // method with the corrected id before control returns here — bail out
    // of the stale frame instead of clobbering the corrected state with
    // values derived from the old id (which would leave an active org with
    // no selected org object, and nav links pointing at /null/...).
    this.getSelectedOrg(orgId);
    if (orgId !== this.getSelectedOrgIdValue) {
      return;
    }

    this.updateIsOrgAdmin(orgId);
    this.updateIsOrgActive(orgId);
    this.updateOrgCapabilites(orgId);
    this.updateSelectedOrg(orgId);
  }

  private accessToken = new BehaviorSubject<string>('');
  tokenExpirationTime: number | null = null;
  get $getAccessToken(): Observable<string> {
    return this.accessToken.asObservable();
  }
  public get getAccessTokenValue(): string {
    return this.accessToken.value;
  }
  getTokenExpirationTime(): number | null {
    return this.tokenExpirationTime;
  }

  private orgs = new BehaviorSubject<UIOrg[]>([]);
  get $getOrgs(): Observable<UIOrg[]> {
    return this.orgs.asObservable();
  }
  public get getOrgsValue(): UIOrg[] {
    return this.orgs.value;
  }

  private roles: string[] = [];

  private isAdmin = new BehaviorSubject<boolean>(false);
  get $isAdmin(): Observable<boolean> {
    return this.isAdmin.asObservable();
  }
  public get isAdminValue(): boolean {
    return this.isAdmin.value;
  }

  private isOrgAdmin = new BehaviorSubject<boolean>(false);
  get $isOrgAdmin(): Observable<boolean> {
    return this.isOrgAdmin.asObservable();
  }
  public get isOrgAdminValue(): boolean {
    return this.isOrgAdmin.value;
  }

  private palMode = new BehaviorSubject<boolean>(false);
  get $palMode(): Observable<boolean> {
    return this.palMode.asObservable();
  }
  public get palModeValue(): boolean {
    return this.palMode.value;
  }
  public setPalMode(value: boolean) {
    this.palMode.next(value);
  }

  private updateIsOrgAdmin(orgId: string) {
    const isOrgAdmin = this.roles.includes(orgId + ':admin') || this.roles.includes('*:admin');
    this.isOrgAdmin.next(isOrgAdmin);
  }

  private isOrgActive = new BehaviorSubject<boolean>(true);
  get $isOrgActive(): Observable<boolean> {
    return this.isOrgActive.asObservable();
  }
  public get isOrgActiveValue(): boolean {
    return this.isOrgActive.value;
  }
  private updateIsOrgActive(orgId: string) {
    let active = false;
    const thisOrg = this.getSelectedOrg(orgId);
    if (thisOrg && thisOrg.status === 'active') {
      active = true;
    }
    this.isOrgActive.next(active);
  }

  private orgCapabilites = new BehaviorSubject<String[]>([]);
  get $getOrgCapabilites(): Observable<String[]> {
    return this.orgCapabilites.asObservable();
  }
  public get getOrgCapabilitesValue(): String[] {
    return this.orgCapabilites.value;
  }
  private updateOrgCapabilites(orgId: string) {
    let capabilities: String[] = [];
    const thisOrg = this.getSelectedOrg(orgId);
    Object.entries(thisOrg?.settings || {}).forEach(([key, value]) => {
      if (value?.capability) {
        capabilities.push(key);
      }
    });
    this.orgCapabilites.next(capabilities);
  }


  get $redirectUrl(): string {
    return this.redirectUrl;
  }

  public getSelectedOrg(orgId: string = this.getSelectedOrgIdValue): UIOrg | undefined {
    let thisOrg = this.getOrgsValue.find((o) => o.id === orgId);

    // If the selected org couldn't be found, choose the first org available. If no
    //  orgs, this will return undefined
    if (!thisOrg) {
      if (this.getOrgsValue.length > 0) {
        this.setSelectedOrgId(this.getOrgsValue[0].id);
        thisOrg = this.getOrgsValue[0];
      }
    }
    return thisOrg;
  }

  get email(): string | undefined {
    var user = localStorage.getItem('user');
    if (user) {
      var u = JSON.parse(user);
      var email: string = u.email;
      return email;
    } else {
      return undefined;
    }
  }

  adminHidden: boolean = false;
  constructor(
    private sessionService: SessionService,
    private router: Router,
    private notify: NotificationService,
    private userService: UsersService,
    private socialAuthService: SocialAuthService,
    private orgService: OrganizationsService,
    private statsigService: StatsigService
  ) {
    this.checkLogin();

    this.accessToken.subscribe((token) => {
      if (token) {
        const helper = new JwtHelperService();
        const decodedToken = helper.decodeToken(token);
        if (decodedToken && decodedToken.exp) {
          this.tokenExpirationTime = decodedToken.exp * 1000;
        }
      } else {
        this.tokenExpirationTime = null;
      }
    });

    this.$getSelectedOrgId.subscribe((orgId) => {
      // Skip empty ids, and stale frames: during a re-entrant self-heal
      // (see refreshSelectedOrgState) the corrected id has already been
      // processed by the time control returns to the frame that emitted
      // the stale id.
      if (!orgId || orgId !== this.getSelectedOrgIdValue) {
        return;
      }
      this.refreshSelectedOrgState();
    });

    // If the user is logged in, grab everything from local storage
    if (this.isLoggedInValue) {
      // Try to grab everything from local storage
      this.accessToken.next(localStorage.getItem('accessToken') || '');
      this.user.next(JSON.parse(localStorage.getItem('user') || '{}'));
      this.orgs.next(JSON.parse(localStorage.getItem('orgs') || '[]'));
      this.roles = JSON.parse(localStorage.getItem('roles') || '[]');

      this.selectedOrgId.next(localStorage.getItem('selectedOrgId') || '');

      // Check if the user has internal ledger roles
      var services = JSON.parse(localStorage.getItem('services') || '[]');
      if (services.length > 0) {
        this.isAdmin.next(true);
      }
    } else {
      this.clearLocalStorage();
    }
  }

  public toggleAdminVisibility() {
    if (this.isAdminValue || this.adminHidden) {
      // So swap the values
      this.isAdmin.next(this.adminHidden);
      this.adminHidden = !this.adminHidden;
    }
  }

  private refreshSessionObservable: Observable<any> | null = null;
  public refreshSession() {
    if (this.refreshSessionObservable) {
      return this.refreshSessionObservable;
    }

    let rt = localStorage.getItem('refreshToken');
    if (rt) {
      var sessionBody: PostSessionRequest = {
        grantType: 'refreshToken',
        email: this.getUserValue.email,
        refreshToken: rt,
      };

      return this.sessionService.postSession(sessionBody).pipe(
        map((r: any) => {
          this.processNewSession(r);
        }),
        shareReplay(1),
        finalize(() => {
          this.refreshSessionObservable = null;
        })
      );
    } else {
      return EMPTY;
    }
  }

  private clearLocalStorage() {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('orgs');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('services');
    localStorage.removeItem('roles');
    this.orgs.next([]);
  }

  public resp: Observable<any>;
  loginV2(email: string, password: string) {
    var sessionBody: PostSessionRequest = {
      grantType: 'password',
      email: email,
      password: password,
    };

    return this.sessionService.postSession(sessionBody).pipe(
      map((resp) => {
        this.setPalMode(false);
        this.clearLocalStorage();
        this.processNewSession(resp);
      })
    );
  }

  loginGoogle(idToken: string) {
    var sessionBody: PostSessionGoogleRequest = {
      idToken: idToken,
    };

    return this.sessionService.postSessionGoogle(sessionBody).pipe(
      map((resp) => {
        this.setPalMode(false);
        this.clearLocalStorage();
        this.processNewSession(resp);
      })
    );
  }

  createUserWithGoogle(
    idToken: string,
    inviteToken: string | undefined,
    orgName: string | undefined
  ) {
    const req: SignupWithGoogleRequest = {
      idToken: idToken,
      inviteToken: inviteToken,
      orgName: orgName,
    };

    return this.userService.signupWithGoogle(req).pipe(
      map((resp) => {
        this.setPalMode(false);
        this.clearLocalStorage();
        this.processNewSession(resp);
      })
    );
  }

  loginMicrosoft(idToken: string) {
    const sessionBody = {
      idToken: idToken,
    };

    return this.sessionService.postSessionMicrosoft(sessionBody).pipe(
      map((resp) => {
        this.setPalMode(false);
        this.clearLocalStorage();
        this.processNewSession(resp);
      })
    );
  }

  createUserWithMicrosoft(
    idToken: string,
    inviteToken: string | undefined,
    orgName: string | undefined
  ) {
    const req: SignupWithMicrosoftRequest = {
      idToken: idToken,
      inviteToken: inviteToken,
      orgName: orgName,
    };

    return this.userService.signupWithMicrosoft(req).pipe(
      map((resp) => {
        this.setPalMode(false);
        this.clearLocalStorage();
        this.processNewSession(resp);
      })
    );
  }

  private async processNewSession(session: any) {
    // Set a bunch of stuff in local storage
    localStorage.setItem('accessToken', session.accessToken);
    localStorage.setItem('refreshToken', session.refreshToken);
    localStorage.setItem('user', JSON.stringify(session.user));
    if (session.services) localStorage.setItem('services', JSON.stringify(session.services));

    var orgsToSet = [];
    // Sort the orgs
    if (session.orgs) {
      orgsToSet = session.orgs.sort((a: UIOrg, b: UIOrg) => a.name.localeCompare(b.name));
      localStorage.setItem('orgs', JSON.stringify(orgsToSet));
    }

    // If the user has any administrative orgs in the list (internal only), add them to the current orgs list
    this.getOrgsValue.forEach((org) => {
      if (org.name.endsWith('(observing)')) {
        orgsToSet.push(org);
      }
    });

    // Update relevant observables
    this.accessToken.next(session.accessToken);
    this.orgs.next(orgsToSet);
    this.user.next(session.user);

    // Check if the selected org is one the user still has access to
    if (localStorage.getItem('selectedOrgId')) {
      if (
        orgsToSet &&
        !orgsToSet.some((o: any) => {
          return o.id == localStorage.getItem('selectedOrgId');
        })
      ) {
        localStorage.removeItem('selectedOrgId');
      }
    }

    // Set the roles array from the JWT
    const helper = new JwtHelperService();
    const decodedToken = helper.decodeToken(this.getAccessTokenValue);
    if (decodedToken.roles) {
      localStorage.setItem('roles', JSON.stringify(decodedToken.roles));
      this.roles = decodedToken.roles;
    }

    // Ensure selected org is set
    const orgIdBefore = this.getSelectedOrgIdValue;
    if (!localStorage.getItem('selectedOrgId')) {
      if (session.orgs && session.orgs.length > 0) {
        localStorage.setItem('selectedOrgId', session.orgs[0].id);
        this.setSelectedOrgId(session.orgs[0].id);
      }
    } else {
      this.setSelectedOrgId(localStorage.getItem('selectedOrgId') || '');
    }

    // The org list was just replaced. When the id changed above,
    // setSelectedOrgId() emitted synchronously and its subscription has
    // already recomputed the org-derived state against the fresh orgs.
    // When it didn't change, that call early-returned and the state still
    // reflects the pre-refresh org list — possibly an empty one on a cold
    // load, which leaves the org picker blank and org-scoped nav links
    // broken. Recompute only in that case: a second pass would re-emit
    // selectedOrg, and subscribers rebuild usage streams on every emission.
    if (this.getSelectedOrgIdValue === orgIdBefore) {
      this.refreshSelectedOrgState();
    }

    this.loggedIn.next(true);

    try {
      await this.statsigService.updateUserAsync({
        userID: this.getUserValue.id,
        email: this.getUserValue.email,
        appVersion: environment.appVersion,
        custom: {
          is_employee: this.email ? this.email.endsWith('livedatatechnologies.com') : false,
        },
      });
    } catch (e) {
      Log.s('AuthService - error initializing statsig user', 'error');
    }

    // Check if the user has internal ledger roles
    // TOOD: make this more flexible in case users have admin on other services
    if (session.services) {
      this.isAdmin.next(true);
    }
  }

  // Check if a user has a given role on the current org
  // Deals with hierarchy of roles, so you can call this with "viewer" and it will also check higher-level roles
  public userHasRole(requiredRole: string): boolean {
    let roles: string[] = [requiredRole];
    if (requiredRole === 'viewer') {
      roles.push('admin');
      roles.push('editor');
    } else if (requiredRole === 'editor') {
      roles.push('admin');
    }
    return this.userHasRoles(roles);
  }

  // Check if a user has one of any allowed roles, on an org or globally
  private userHasRoles(requiredRoles: Array<string>): boolean {
    return requiredRoles.some(
      (role) =>
        this.roles.includes(this.getSelectedOrgIdValue + ':' + role) ||
        this.roles.includes('*:' + role)
    );
  }

  // From the resolver that ensures the org is loaded and matches the orgId in the path
  public setOrgFromPath(orgId: string) {
    if (orgId === this.getSelectedOrgIdValue) {
      return;
    }

    let org = this.getOrgsValue.find((o) => o.id === orgId);

    if (org) {
      this.setSelectedOrgId(orgId);
      return;
    }

    if (!this.isAdminValue) {
      this.notify.error(
        'You do not have access to this organization. Please ask to be invited by your administrator.'
      );
      this.router.navigateByUrl('/main');
      return;
    }

    // LDT admins can go get any org they want
    this.orgService.getOrg(orgId).subscribe({
      next: (org) => {
        this.setAdministrativeOrg(org);
      },
      error: () => {
        this.notify.error('Error getting organization: ' + orgId);
        this.router.navigateByUrl('/main');
      },
    });
  }

  // Adds an "observed" org to the current user's org list so they can interact with it
  public setAdministrativeOrg(org: UIOrg | Org) {
    // Change the name so we know we're observing, and push it to the org list for the user
    org.name = org.name + ' (observing)';
    const uiOrg = org as UIOrg;
    uiOrg.role = 'admin';
    this.getOrgsValue.push(uiOrg);
    this.orgs.next(this.getOrgsValue);
    this.setSelectedOrgId(org.id);
  }

  public validateUserEmail(token: string) {
    let body: ValidateUserEmailRequest = { token: token };
    this.userService.validateUserEmail(this.getUserValue.id, body).subscribe(
      (r) => {
        // Nothing to do here
      },
      () => {
        // Fail silently
      }
    );
  }

  setRedirectUrl(redirectUrl: string): void {
    if (redirectUrl !== '/login' && redirectUrl !== '/signup') {
      this.redirectUrl = redirectUrl;
    } else {
      this.redirectUrl = '/main';
    }
  }
  public logOut() {
    this.accessToken.next('');
    this.isAdmin.next(false);
    this.clearLocalStorage();
    try {
      // Total hack here, but using the built-in signOut method doesn't work well.
      // Esp for MS, it pops open a MS logout window and we really don't want that.
      // So, we're internally broadcasting null here which will reset the state
      (this.socialAuthService as any)._authState.next(null);
    } catch (e: any) {
      // Nothing to do if this fails
    }

    this.loggedIn.next(false);
    this.notify.info('You have been logged out. See you soon.');

    // navigate after 1 second
    setTimeout(() => {
      this.router.navigateByUrl('/login');
    }, 1000);
  }
}
